import { Component } from '@theme/component';

/**
 * Slider Stories section custom element.
 *
 * Canonical Swiper pattern (matches ax-tabbed-product-lists, ax-usage-tips):
 * `<swiper-container init="false">` in Liquid, this component calls
 * `.initialize()` once at connect with breakpoints config.
 *
 *   mobile (< 750px): 2.2 slides visible (peek), pagination dots, swipe enabled
 *   desktop (>= 750px): 3 slides side-by-side, swipe + pagination disabled
 *
 * Pagination is rendered in light DOM via `pagination.el` so we can style it
 * with classic CSS (no shadow ::part). Bullet styles are global in base.css.
 *
 * After init, dispatches `removeLazyKeep` on the swiper container so all
 * slide media load together — no flash on swipe (canonical Horizon pattern
 * from observer-CFH.liquid).
 *
 * Skips entirely when the section is in "join" mode (no carousel rendered).
 *
 * @extends {Component}
 */
class AxSliderStories extends Component {
	/** @type {any} */
	#swiper = null;

	/** @type {HTMLDialogElement[]} */
	#dialogs = [];

	async connectedCallback() {
		super.connectedCallback();
		this.#setupDialogs();
		await customElements.whenDefined('swiper-container');
		this.#initSwiper();
	}

	disconnectedCallback() {
		super.disconnectedCallback();
		try {
			this.#swiper?.destroy(true, true);
		} catch (_) {
			// Swiper can throw during teardown if already destroyed.
		}
		this.#swiper = null;
		document.documentElement.style.overflow = '';
	}

	#initSwiper() {
		const swiperEl = /** @type {HTMLElement & { swiper: any; initialize: () => void }} */ (
			this.querySelector('swiper-container.ax-slider-stories__swiper')
		);
		if (!swiperEl || swiperEl.swiper) return;

		Object.assign(swiperEl, {
			slidesPerView: 1.6,
			spaceBetween: 12,
			grabCursor: true,
			a11y: true,
			keyboard: { enabled: true },
			pagination: false,
			// Center the slide(s) when there are fewer than slidesPerView (e.g.
			// single story on desktop).
			centerInsufficientSlides: true,
			breakpoints: {
				990: {
					slidesPerView: 3,
					spaceBetween: 12,
					allowTouchMove: false,
				},
			},
		});

		swiperEl.initialize();
		swiperEl.removeAttribute('init');
		this.#swiper = swiperEl.swiper;

		// Preload all CFH-lazy slides at init. The IntersectionObserver can't
		// detect off-viewport slides in an overflow:hidden carousel, so we
		// force-load every [data-lazy-keep] descendant now.
		document.dispatchEvent(
			new CustomEvent('removeLazyKeep', {
				detail: { lazyParentElement: swiperEl },
			}),
		);
	}

	#setupDialogs() {
		this.#dialogs = /** @type {HTMLDialogElement[]} */ (
			[...this.querySelectorAll('dialog.ax-slider-stories__dialog')]
		);
		for (const dialog of this.#dialogs) {
			// Clic sur le backdrop → fermeture.
			dialog.addEventListener('click', (event) => {
				if (event.target === dialog) dialog.close();
			});
			// Fermeture (croix, Échap, backdrop) → pause + report du temps sur la tile.
			dialog.addEventListener('close', () => this.#onDialogClose(dialog));
		}
	}

	/**
	 * Ouvre la vidéo de la tile en modale, calée sur l'instant courant de la
	 * vidéo ambiante de la tile (source unique → continuité fluide).
	 * @param {Event} event
	 */
	openVideo(event) {
		const target = /** @type {HTMLElement | null} */ (event.target);
		const trigger = target?.closest('[data-story-dialog]');
		if (!(trigger instanceof HTMLElement)) return;

		const dialog = this.querySelector(`#${CSS.escape(trigger.dataset.storyDialog ?? '')}`);
		if (!(dialog instanceof HTMLDialogElement)) return;

		const modalVideo = dialog.querySelector('video');
		const tileVideo = trigger
			.closest('.ax-slider-stories__tile')
			?.querySelector('.ax-slider-stories__media-video');

		if (modalVideo instanceof HTMLVideoElement && tileVideo instanceof HTMLVideoElement) {
			const time = tileVideo.currentTime;
			if (Number.isFinite(time)) {
				const seek = () => {
					try {
						modalVideo.currentTime = time;
					} catch (_) {
						// currentTime peut throw si la source n'est pas encore prête.
					}
				};
				if (modalVideo.readyState >= 1) seek();
				else modalVideo.addEventListener('loadedmetadata', seek, { once: true });
			}
			modalVideo.muted = false;
		}

		dialog.showModal();
		// Scroll-lock via style inline : l'attribut `scroll-lock` est réconcilié
		// par le gestionnaire de dialogs du thème (ce <dialog> natif n'est pas géré).
		document.documentElement.style.overflow = 'hidden';
		if (modalVideo instanceof HTMLVideoElement) modalVideo.play().catch(() => {});
	}

	/** @param {Event} event */
	closeVideo(event) {
		const target = /** @type {HTMLElement | null} */ (event.target);
		const dialog = target?.closest('dialog.ax-slider-stories__dialog');
		if (dialog instanceof HTMLDialogElement && dialog.open) dialog.close();
	}

	/** @param {HTMLDialogElement} dialog */
	#onDialogClose(dialog) {
		const modalVideo = dialog.querySelector('video');
		const trigger = this.querySelector(`[data-story-dialog="${dialog.id}"]`);
		const tileVideo = trigger
			?.closest('.ax-slider-stories__tile')
			?.querySelector('.ax-slider-stories__media-video');

		if (modalVideo instanceof HTMLVideoElement) {
			if (tileVideo instanceof HTMLVideoElement && Number.isFinite(modalVideo.currentTime)) {
				try {
					tileVideo.currentTime = modalVideo.currentTime;
				} catch (_) {
					// no-op
				}
			}
			modalVideo.pause();
		}

		document.documentElement.style.overflow = '';
	}
}

if (!customElements.get('ax-slider-stories')) {
	customElements.define('ax-slider-stories', AxSliderStories);
}
